import { useState, type FormEvent } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import { ApiError } from "../lib/api.js";
import {
  createDecision,
  deleteDecision,
  listDecisions,
} from "../lib/api-modules/decisions.js";
import type {
  Decision,
  MembershipRole,
  NoteRelatedEntityType,
} from "../lib/api-modules/types.js";
import { ErrorMessage } from "./auth/error-message.js";
import { ConfirmButton } from "./confirm-button.js";

/**
 * Polymorphic decisions panel — embeds in any entity drilldown:
 *
 *   <DecisionsPanel entityType="account" entityId={accountId} />
 *
 * Decisions are mostly append-only. The inline form captures the
 * historical record (title / context / chosen_option / rationale +
 * optional revisit_date). PATCH only mutates revisit_date / status /
 * is_archived per the locked Phase 4.2 semantics.
 *
 * v1 scope cut: filters this panel to decisions attached to THIS
 * (entityType, entityId). The list endpoint Queries the household and
 * filters in JS — at v1 scale (handful of decisions) that's the
 * cleanest path.
 */

const READ_ONLY_ROLES: ReadonlySet<MembershipRole> = new Set([
  "viewer",
  "auditor",
]);

export interface DecisionsPanelProps {
  entityType: NoteRelatedEntityType;
  entityId: string;
  canWriteOverride?: boolean;
}

export function DecisionsPanel({
  entityType,
  entityId,
  canWriteOverride,
}: DecisionsPanelProps) {
  const { user } = useAuth();
  if (!user) return null;
  const canWrite =
    canWriteOverride ?? !READ_ONLY_ROLES.has(user.role);

  const queryKey = ["decisions", "by-household"] as const;
  const query = useQuery({
    queryKey,
    queryFn: () => listDecisions(),
  });

  const filtered =
    query.data?.decisions.filter(
      (d) =>
        d.related_entity_type === entityType &&
        d.related_entity_id === entityId,
    ) ?? [];

  return (
    <section
      aria-labelledby={`decisions-panel-${entityId}`}
      className="mt-4 rounded-md border border-zinc-200 p-4 dark:border-zinc-800"
      data-testid="decisions-panel"
    >
      <h3
        id={`decisions-panel-${entityId}`}
        className="mb-3 text-sm font-semibold uppercase tracking-wide text-zinc-600 dark:text-zinc-400"
      >
        Decisions
      </h3>

      {canWrite && (
        <CreateDecisionForm
          entityType={entityType}
          entityId={entityId}
          queryKey={queryKey}
        />
      )}

      <div className="mt-3 space-y-2">
        {query.isPending && (
          <p className="text-sm text-zinc-500">Loading…</p>
        )}
        {query.isError && (
          <ErrorMessage>
            Couldn't load decisions:{" "}
            {query.error instanceof Error ? query.error.message : "unknown"}
          </ErrorMessage>
        )}
        {query.data && filtered.length === 0 && (
          <p className="text-sm text-zinc-500">No decisions logged.</p>
        )}
        {filtered.length > 0 && (
          <ul className="space-y-2">
            {filtered.map((d) => (
              <li
                key={d.decision_id}
                className="rounded-md border border-zinc-200 px-3 py-2 dark:border-zinc-800"
                data-testid={`decision-${d.decision_id}`}
              >
                <DecisionCard
                  decision={d}
                  canWrite={canWrite}
                  queryKey={queryKey}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function DecisionCard({
  decision,
  canWrite,
  queryKey,
}: {
  decision: Decision;
  canWrite: boolean;
  queryKey: readonly unknown[];
}) {
  const queryClient = useQueryClient();
  const archive = useMutation({
    mutationFn: () => deleteDecision(decision.decision_id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({
        queryKey: ["decisions", "revisit"] as const,
      });
    },
  });

  return (
    <div className="text-sm">
      <div className="flex items-baseline justify-between gap-3">
        <h4 className="font-medium text-zinc-900 dark:text-zinc-100">
          {decision.title}
        </h4>
        <span className="text-xs text-zinc-500">
          {new Date(decision.decision_made_at).toLocaleDateString()}
          {decision.status !== "active" && (
            <span className="ml-2 italic">{decision.status}</span>
          )}
        </span>
      </div>
      <p className="mt-1 whitespace-pre-wrap text-zinc-700 dark:text-zinc-300">
        <span className="font-medium">Chose:</span> {decision.chosen_option}
      </p>
      <p className="mt-1 whitespace-pre-wrap text-xs text-zinc-600 dark:text-zinc-400">
        {decision.rationale}
      </p>
      {decision.revisit_date && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
          Revisit by {decision.revisit_date}
        </p>
      )}
      {canWrite && (
        <div className="mt-2 flex justify-end">
          <ConfirmButton
            label="Archive"
            size="xs"
            onConfirm={() => archive.mutate()}
            isPending={archive.isPending}
          />
        </div>
      )}
    </div>
  );
}

function CreateDecisionForm({
  entityType,
  entityId,
  queryKey,
}: {
  entityType: NoteRelatedEntityType;
  entityId: string;
  queryKey: readonly unknown[];
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [chosen, setChosen] = useState("");
  const [rationale, setRationale] = useState("");
  const [context, setContext] = useState("");
  const [revisitDate, setRevisitDate] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      createDecision({
        title: title.trim(),
        context: context.trim() || title.trim(),
        chosen_option: chosen.trim(),
        rationale: rationale.trim(),
        related_entity_type: entityType,
        related_entity_id: entityId,
        ...(revisitDate ? { revisit_date: revisitDate } : {}),
      }),
    onSuccess: async () => {
      setTitle("");
      setChosen("");
      setRationale("");
      setContext("");
      setRevisitDate("");
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({
        queryKey: ["decisions", "revisit"] as const,
      });
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : "Couldn't create decision. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    mutation.mutate();
  }

  const submitDisabled =
    mutation.isPending ||
    title.trim().length === 0 ||
    chosen.trim().length === 0 ||
    rationale.trim().length === 0;

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-2">
      <input
        type="text"
        aria-label="Decision title"
        placeholder="Decision title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={200}
        className="w-full rounded-md border border-zinc-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
        disabled={mutation.isPending}
      />
      <input
        type="text"
        aria-label="Chosen option"
        placeholder="What we chose"
        value={chosen}
        onChange={(e) => setChosen(e.target.value)}
        maxLength={2_000}
        className="w-full rounded-md border border-zinc-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
        disabled={mutation.isPending}
      />
      <textarea
        aria-label="Rationale"
        placeholder="Why we chose it"
        value={rationale}
        onChange={(e) => setRationale(e.target.value)}
        rows={2}
        maxLength={20_000}
        className="w-full resize-y rounded-md border border-zinc-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
        disabled={mutation.isPending}
      />
      <textarea
        aria-label="Context (optional)"
        placeholder="Context (optional — defaults to title)"
        value={context}
        onChange={(e) => setContext(e.target.value)}
        rows={2}
        maxLength={20_000}
        className="w-full resize-y rounded-md border border-zinc-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
        disabled={mutation.isPending}
      />
      <div className="flex items-center gap-2">
        <label
          htmlFor="decision-revisit-date"
          className="text-xs text-zinc-600 dark:text-zinc-400"
        >
          Revisit:
        </label>
        <input
          id="decision-revisit-date"
          type="date"
          value={revisitDate}
          onChange={(e) => setRevisitDate(e.target.value)}
          className="rounded-md border border-zinc-300 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-900"
          disabled={mutation.isPending}
        />
        <button
          type="submit"
          disabled={submitDisabled}
          className="ml-auto rounded-md bg-brand-500 px-3 py-1 text-xs font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? "Saving…" : "Log decision"}
        </button>
      </div>
      {inlineError && <ErrorMessage>{inlineError}</ErrorMessage>}
    </form>
  );
}
